import { useEffect, useRef, useCallback } from 'react';
import { NOTE_FREQUENCIES } from '../constants/gameConstants';

export const useAudioContext = () => {
  const audioContextRef = useRef(null);
  
  useEffect(() => {
    // Initialize Web Audio context
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Play musical note
  const playNote = useCallback((noteName, volumeMultiplier = 1.0) => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    
    // Resume context if suspended (browsers require user interaction)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const frequency = NOTE_FREQUENCIES[noteName] || 440;
    
    // Create oscillator
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine'; // Smooth tone
    
    // Envelope: quick attack, short sustain, quick release
    // Apply volume multiplier to all gain values
    const now = ctx.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3 * volumeMultiplier, now + 0.01); // Attack
    gainNode.gain.linearRampToValueAtTime(0.2 * volumeMultiplier, now + 0.1); // Sustain
    gainNode.gain.linearRampToValueAtTime(0, now + 0.4); // Release
    
    oscillator.start(now);
    oscillator.stop(now + 0.4);
  }, []);

  // Play tone with specific volume (for volume dimension)
  const playToneWithVolume = useCallback((volume) => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    
    // Resume context if suspended (browsers require user interaction)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    // Use middle C (C4) frequency
    const frequency = NOTE_FREQUENCIES['C4'] || 261.63;
    
    // Create oscillator
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine'; // Smooth tone
    
    // Envelope with specified volume
    const now = ctx.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume * 0.3, now + 0.01); // Attack
    gainNode.gain.linearRampToValueAtTime(volume * 0.2, now + 0.1); // Sustain
    gainNode.gain.linearRampToValueAtTime(0, now + 0.4); // Release
    
    oscillator.start(now);
    oscillator.stop(now + 0.4);
  }, []);

  return { playNote, playToneWithVolume };
};

